// NearMe widget — uses navigator.geolocation + haversine to surface
// the 5 closest service locations. Pure client component; renders a
// stable SSR fallback (5 flagship branches) so hydration is clean.
import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Navigation, MapPin, Loader2 } from "lucide-react";
import { locations, type Location } from "@/data/locations";

const DEFAULT_FALLBACK: Location[] = locations
  .filter((l) => l.type === "headquarters" || l.type === "branch")
  .slice(0, 5);

function haversineKm(a: { lat: number; lng: number }, b: { lat: number; lng: number }) {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

type WithDistance = Location & { distanceKm?: number };

export function NearMeWidget() {
  const [state, setState] = useState<"idle" | "loading" | "ok" | "denied" | "error">("idle");
  const [results, setResults] = useState<WithDistance[]>(DEFAULT_FALLBACK);

  function locate() {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setState("error");
      return;
    }
    setState("loading");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const me = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        const ranked = locations
          .filter((l): l is Location & { lat: number; lng: number } => typeof l.lat === "number" && typeof l.lng === "number")
          .map((l) => ({ ...l, distanceKm: haversineKm(me, { lat: l.lat, lng: l.lng }) }))
          .sort((a, b) => (a.distanceKm ?? 0) - (b.distanceKm ?? 0))
          .slice(0, 5);
        setResults(ranked);
        setState("ok");
      },
      () => setState("denied"),
      { enableHighAccuracy: false, maximumAge: 60_000, timeout: 8000 },
    );
  }

  // Auto-restore last permission silently (no prompt) so returning users
  // see fresh nearby results on mount.
  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.permissions) return;
    navigator.permissions
      .query({ name: "geolocation" as PermissionName })
      .then((p) => {
        if (p.state === "granted") locate();
      })
      .catch(() => {});
  }, []);

  return (
    <section className="rounded-3xl border border-gold/25 bg-gradient-to-br from-gold/[0.08] via-white/[0.03] to-transparent p-6 sm:p-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-gold/90">Near Me · ใกล้คุณที่สุด</div>
          <h2 className="mt-1 text-xl font-extrabold text-white sm:text-2xl">หาสาขาที่ใกล้คุณ</h2>
          <p className="mt-1 text-sm text-white/70">
            กดปุ่มเพื่อให้เบราว์เซอร์ส่งพิกัด เราจะคำนวณ Top 5 จุดบริการที่ใกล้ที่สุดให้ทันที (ไม่บันทึก/ไม่ส่งออก)
          </p>
        </div>
        <button
          type="button"
          onClick={locate}
          disabled={state === "loading"}
          className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-gold px-4 py-2 text-sm font-bold text-navy-deep transition hover:bg-gold/90 disabled:opacity-60"
        >
          {state === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Navigation className="h-4 w-4" />}
          {state === "loading" ? "กำลังคำนวณ…" : state === "ok" ? "อัปเดตอีกครั้ง" : "ใช้ตำแหน่งของฉัน"}
        </button>
      </div>

      {state === "denied" && (
        <p className="mt-4 rounded-lg border border-amber-400/30 bg-amber-400/10 px-3 py-2 text-xs text-amber-200">
          เบราว์เซอร์ปฏิเสธการเข้าถึงตำแหน่ง — แสดงสาขาหลักทั้ง 4 แทน
        </p>
      )}
      {state === "error" && (
        <p className="mt-4 rounded-lg border border-rose-400/30 bg-rose-400/10 px-3 py-2 text-xs text-rose-200">
          อุปกรณ์ไม่รองรับ Geolocation
        </p>
      )}

      <ul className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {results.map((l) => (
          <li key={l.slug}>
            <Link
              to="/locations/$slug"
              params={{ slug: l.slug }}
              className="flex h-full items-start gap-2 rounded-2xl border border-white/10 bg-white/[0.04] p-3 transition hover:border-gold/40 hover:bg-white/[0.07]"
            >
              <MapPin className="mt-0.5 h-4 w-4 text-gold" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-bold text-white">{l.nameTh}</div>
                <div className="truncate text-[11px] text-white/80">{l.nameEn}</div>
                {typeof l.distanceKm === "number" && (
                  <div className="mt-0.5 text-[11px] font-bold text-gold">{l.distanceKm.toFixed(1)} กม.</div>
                )}
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
